import React, { useEffect, useRef, useState } from 'react';
import { SOCIAL_PLATFORMS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetContent } from '~/hooks/api/useGetContent';
import { CONTENT_TYPES } from '~/searchApi/content/searchContentApi.constants';
import {
  ASSET_TYPE,
  IApiSearchContent,
  IApiSearchYoutubeContent,
} from '~/searchApi/content/searchContentApi.model';
import { TTAContent } from '~/types/tta';

interface SelectedContentProps {
  content: TTAContent;
}

export const SelectedContent: React.FC<SelectedContentProps> = ({
  content,
}) => {
  const { data } = useGetContent(content.content_id);
  const contentData = data?.data?.[0];
  const contentType = (contentData as any)?.contentType;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Simplified drag functionality states
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  // Drag threshold in pixels - only start dragging after moving this distance
  const DRAG_THRESHOLD = 30; // Increased threshold for more intentional swipes
  const carouselRef = useRef<HTMLDivElement>(null);

  const generatePreviewUrl = (
    contentData: IApiSearchContent,
    asset?: any,
  ): string => {
    if (contentData.platform === SOCIAL_PLATFORMS.youtube) {
      const youtubeContent = contentData as IApiSearchYoutubeContent;
      return (
        youtubeContent.thumbnails?.standard?.url ||
        contentData.assets[3]?.url ||
        ''
      );
    }
    return asset?.previewUrl || '';
  };

  const isCarousel = contentType === CONTENT_TYPES.CAROUSEL_ALBUM;
  const assets = contentData?.assets || [];
  const currentAsset = assets[currentSlideIndex] || assets[0];
  const isVideo = currentAsset?.type === ASSET_TYPE.video;
  const previewUrl = contentData
    ? generatePreviewUrl(contentData, currentAsset)
    : '';
  const videoUrl = currentAsset?.url;

  const handlePlayClick = () => {
    if (isVideo && videoUrl) {
      // Track video play action
      trackEvent('foam-tta-selected-content-video-play', {
        contentId: content.content_id,
        assetType: currentAsset?.type || 'unknown',
        slideIndex: currentSlideIndex,
        totalSlides: assets.length,
        isCarousel,
      });

      setIsPlaying(true);
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      const wasPaused = videoRef.current.paused;

      if (wasPaused) {
        videoRef.current.play();
        setIsPaused(false);

        // Track video resume
        trackEvent('foam-tta-selected-content-video-resume', {
          contentId: content.content_id,
          slideIndex: currentSlideIndex,
          totalSlides: assets.length,
        });
      } else {
        videoRef.current.pause();
        setIsPaused(true);

        // Track video pause
        trackEvent('foam-tta-selected-content-video-pause', {
          contentId: content.content_id,
          slideIndex: currentSlideIndex,
          totalSlides: assets.length,
        });
      }
    }
  };

  const handlePlayIconClick = () => {
    if (isVideo && videoUrl) {
      if (isPaused && videoRef.current) {
        videoRef.current.play();
        setIsPaused(false);

        // Track video resume via icon
        trackEvent('foam-tta-selected-content-video-resume-icon', {
          contentId: content.content_id,
          slideIndex: currentSlideIndex,
          totalSlides: assets.length,
        });
      } else {
        // Track initial video play via icon
        trackEvent('foam-tta-selected-content-video-play-icon', {
          contentId: content.content_id,
          assetType: currentAsset?.type || 'unknown',
          slideIndex: currentSlideIndex,
          totalSlides: assets.length,
          isCarousel,
        });

        setIsPlaying(true);
      }
    }
  };

  // Simple slide change function
  const changeSlide = (newIndex: number) => {
    const previousIndex = currentSlideIndex;
    setCurrentSlideIndex(newIndex);

    // Track slide change
    trackEvent('foam-tta-selected-content-slide-change', {
      contentId: content.content_id,
      previousIndex,
      newIndex,
      totalSlides: assets.length,
      direction: newIndex > previousIndex ? 'next' : 'previous',
      previousAssetType: assets[previousIndex]?.type || 'unknown',
      newAssetType: assets[newIndex]?.type || 'unknown',
    });

    // Reset video state when changing slides
    setIsPlaying(false);
    setIsPaused(false);

    // Animate to new position
    if (carouselRef.current) {
      const newX = -newIndex * 200;
      carouselRef.current.style.transform = `translateX(${newX}px)`;
      carouselRef.current.style.transition = 'transform 0.3s ease-out';
      setTimeout(() => {
        if (carouselRef.current) {
          carouselRef.current.style.transition = '';
        }
      }, 300);
    }
  };

  const handlePrevSlide = () => {
    if (
      isCarousel &&
      assets.length > 1 &&
      !hasDragged &&
      currentSlideIndex > 0
    ) {
      const newIndex = currentSlideIndex - 1;
      changeSlide(newIndex);
    }
  };

  const handleNextSlide = () => {
    if (
      isCarousel &&
      assets.length > 1 &&
      !hasDragged &&
      currentSlideIndex < assets.length - 1
    ) {
      const newIndex = currentSlideIndex + 1;
      changeSlide(newIndex);
    }
  };

  // Simplified drag event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isCarousel || assets.length <= 1) return;

    e.preventDefault();
    e.stopPropagation();

    // Track drag start for carousel content
    trackEvent('foam-tta-selected-content-drag-start', {
      contentId: content.content_id,
      totalSlides: assets.length,
      currentSlide: currentSlideIndex,
      interactionType: 'mouse',
    });

    setIsMouseDown(true);
    setIsDragging(false);
    setHasDragged(false);
    setStartX(e.pageX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown || !isCarousel || assets.length <= 1) return;

    const currentX = e.pageX;
    const distance = Math.abs(currentX - startX);

    // Only start dragging if we've moved beyond the threshold
    if (!isDragging && distance > DRAG_THRESHOLD) {
      setIsDragging(true);
      setHasDragged(true);
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent default behavior once dragging starts
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();

      // Determine swipe direction and change slide
      const endX = e.pageX;
      const diff = startX - endX;

      if (Math.abs(diff) > DRAG_THRESHOLD) {
        let newIndex = currentSlideIndex;
        let slideChanged = false;

        if (diff > 0 && currentSlideIndex < assets.length - 1) {
          // Swiped left - go to next slide (only if not at last slide)
          newIndex = currentSlideIndex + 1;
          slideChanged = true;
          changeSlide(newIndex);
        } else if (diff < 0 && currentSlideIndex > 0) {
          // Swiped right - go to previous slide (only if not at first slide)
          newIndex = currentSlideIndex - 1;
          slideChanged = true;
          changeSlide(newIndex);
        }

        // Track successful swipe
        trackEvent('foam-tta-selected-content-swipe', {
          contentId: content.content_id,
          direction: diff > 0 ? 'left' : 'right',
          previousSlide: currentSlideIndex,
          newSlide: newIndex,
          slideChanged,
          totalSlides: assets.length,
          interactionType: 'mouse',
        });
      }
    }

    setIsMouseDown(false);
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 10);
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isCarousel || assets.length <= 1) return;

    e.preventDefault();
    e.stopPropagation();

    // Track touch drag start for carousel content
    trackEvent('foam-tta-selected-content-drag-start', {
      contentId: content.content_id,
      totalSlides: assets.length,
      currentSlide: currentSlideIndex,
      interactionType: 'touch',
    });

    const touch = e.touches[0];
    setIsMouseDown(true);
    setIsDragging(false);
    setHasDragged(false);
    setStartX(touch.pageX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMouseDown || !isCarousel || assets.length <= 1) return;

    // Always prevent default and stop propagation for touch move when we're tracking
    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const currentX = touch.pageX;
    const distance = Math.abs(currentX - startX);

    if (!isDragging && distance > DRAG_THRESHOLD) {
      setIsDragging(true);
      setHasDragged(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();

      // Determine swipe direction and change slide
      const endX = e.changedTouches[0].pageX;
      const diff = startX - endX;

      if (Math.abs(diff) > DRAG_THRESHOLD) {
        let newIndex = currentSlideIndex;
        let slideChanged = false;

        if (diff > 0 && currentSlideIndex < assets.length - 1) {
          // Swiped left - go to next slide (only if not at last slide)
          newIndex = currentSlideIndex + 1;
          slideChanged = true;
          changeSlide(newIndex);
        } else if (diff < 0 && currentSlideIndex > 0) {
          // Swiped right - go to previous slide (only if not at first slide)
          newIndex = currentSlideIndex - 1;
          slideChanged = true;
          changeSlide(newIndex);
        }

        // Track successful touch swipe
        trackEvent('foam-tta-selected-content-swipe', {
          contentId: content.content_id,
          direction: diff > 0 ? 'left' : 'right',
          previousSlide: currentSlideIndex,
          newSlide: newIndex,
          slideChanged,
          totalSlides: assets.length,
          interactionType: 'touch',
        });
      }
    }

    setIsMouseDown(false);
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 10);
  };

  const handleTouchCancel = () => {
    setIsMouseDown(false);
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 10);
  };

  // Stop video when component unmounts or content changes
  useEffect(() => {
    const videoElement = videoRef.current;
    return () => {
      if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
      }
    };
  }, [content.content_id]);

  // Reset playing state when content changes
  useEffect(() => {
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentSlideIndex(0);
  }, [content.content_id]);

  return (
    <div className="mx-auto flex h-[360px] w-[200px] shrink-0 flex-col overflow-hidden rounded-8 bg-neutral-900 tab:mx-0 tab:mr-4">
      <div className="relative size-full overflow-hidden">
        {isCarousel && assets.length > 1 ? (
          /* Carousel with drag support */
          <div
            ref={carouselRef}
            className={`flex h-full ${
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            } select-none`}
            style={
              {
                width: '200px', // Fixed width same as container
                transform: `translateX(${-currentSlideIndex * 200}px)`,
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                touchAction: 'none', // Disable all touch gestures
                WebkitTouchCallout: 'none',
              } as React.CSSProperties
            }
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            onDragStart={(e) => e.preventDefault()}
          >
            {assets.map((asset, index) => {
              const isCurrentVideo = asset?.type === ASSET_TYPE.video;
              const currentPreviewUrl = contentData
                ? generatePreviewUrl(contentData, asset)
                : '';
              const currentVideoUrl = asset?.url;
              const isCurrentSlide = index === currentSlideIndex;

              return (
                <div
                  key={index}
                  className="relative flex h-full w-[200px] shrink-0 items-center justify-center"
                >
                  {isCurrentVideo &&
                  isPlaying &&
                  isCurrentSlide &&
                  currentVideoUrl ? (
                    <video
                      ref={index === currentSlideIndex ? videoRef : null}
                      src={currentVideoUrl}
                      autoPlay
                      className="size-full cursor-pointer object-contain"
                      onEnded={() => {
                        setIsPlaying(false);
                        setIsPaused(false);
                      }}
                      onClick={handleVideoClick}
                      onDragStart={(e) => e.preventDefault()}
                      draggable={false}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    currentPreviewUrl && (
                      <img
                        src={currentPreviewUrl}
                        alt="thumbnail"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="size-full object-contain"
                        onDragStart={(e) => e.preventDefault()}
                        draggable={false}
                      />
                    )
                  )}

                  {/* Video play button overlay */}
                  {isCurrentVideo &&
                    currentVideoUrl &&
                    isCurrentSlide &&
                    (!isPlaying || isPaused) && (
                      <button
                        onClick={(e) => {
                          if (!hasDragged) {
                            if (isPlaying) {
                              handlePlayIconClick();
                            } else {
                              handlePlayClick();
                            }
                          } else {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        className="absolute inset-0 flex items-center justify-center"
                        aria-label="Play video"
                      >
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-neutral-0"
                        >
                          <path
                            d="M8 5v14l11-7z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                        </svg>
                      </button>
                    )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Single asset display */
          <>
            {isVideo && isPlaying && videoUrl ? (
              <video
                ref={videoRef}
                src={videoUrl}
                autoPlay
                className="size-full cursor-pointer object-contain"
                onEnded={() => {
                  setIsPlaying(false);
                  setIsPaused(false);
                }}
                onClick={handleVideoClick}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              previewUrl && (
                <img
                  src={previewUrl}
                  alt="thumbnail"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className="size-full object-contain"
                  onDragStart={(e) => e.preventDefault()}
                  draggable={false}
                />
              )
            )}

            {/* Video play button overlay for single asset */}
            {isVideo && videoUrl && (!isPlaying || isPaused) && (
              <button
                onClick={isPlaying ? handlePlayIconClick : handlePlayClick}
                className="absolute inset-0 flex items-center justify-center"
                aria-label="Play video"
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-neutral-0"
                >
                  <path
                    d="M8 5v14l11-7z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            )}
          </>
        )}

        {/* Carousel navigation arrows */}
        {isCarousel && assets.length > 1 && (
          <>
            {/* Previous arrow - only show if not at first slide */}
            {currentSlideIndex > 0 && (
              <button
                onClick={handlePrevSlide}
                className="absolute left-2 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-100 bg-neutral-0 text-neutral-900 transition-opacity hover:bg-neutral-100"
                aria-label="Previous image"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}

            {/* Next arrow - only show if not at last slide */}
            {currentSlideIndex < assets.length - 1 && (
              <button
                onClick={handleNextSlide}
                className="absolute right-2 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-100 bg-neutral-0 text-neutral-900 transition-opacity hover:bg-neutral-100"
                aria-label="Next image"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </>
        )}

        {/* Carousel indicators */}
        {isCarousel && assets.length > 1 && (
          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
            {assets.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  if (!hasDragged) {
                    changeSlide(index);
                  } else {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                className={`size-2 rounded-100 transition-all ${
                  index === currentSlideIndex
                    ? 'bg-neutral-0'
                    : 'bg-neutral-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
